import { useCallback, useEffect, useRef, useState } from "react";
import client from "../api/client";
import { DEFAULT_FILTER_FIELDS } from "./defaultFilterFields";

const cacheKey = (tenantId, page, teamId) => `filterConfig:${tenantId}:${page}:${teamId || "primary"}`;

const readCache = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeCache = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // cache is best effort only
  }
};

// Fetches the filter popup fields a user should see for an object type. Shows the
// cached config first (offline use), then replaces it with the live response.
function useFilterConfig(page, teamId) {
  const [config, setConfig] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const lastTenantId = useRef(null);
  const lastKey = useRef(null);
  const requestId = useRef(0);

  const fetchConfig = useCallback(async () => {
    const tenantId = localStorage.getItem("currentTenantId");
    const key = cacheKey(tenantId, page, teamId);
    const thisRequest = ++requestId.current;
    setIsLoading(true);

    const cached = readCache(key);
    if (cached) setConfig(cached);
    else if (lastKey.current !== key) setConfig(null);
    lastKey.current = key;

    try {
      const params = { page };
      if (teamId) params.team_id = teamId;
      const response = await client.get("/filter-config/effective", { params });
      if (thisRequest !== requestId.current) return;
      setConfig(response.data);
      setLoadFailed(false);
      writeCache(key, response.data);
    } catch (error) {
      console.error("Error fetching filter config:", error);
      if (thisRequest === requestId.current) setLoadFailed(true);
    } finally {
      if (thisRequest === requestId.current) {
        setIsLoading(false);
        lastTenantId.current = tenantId;
      }
    }
  }, [page, teamId]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  useEffect(() => {
    const handleStorageChange = () => {
      if (localStorage.getItem("currentTenantId") !== lastTenantId.current) fetchConfig();
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [fetchConfig]);

  return {
    config,
    fields: config ? config.fields : loadFailed ? DEFAULT_FILTER_FIELDS[page] || [] : [],
    teams: config ? config.teams : [],
    primaryTeamId: config ? config.primary_team_id : null,
    activeTeamId: config ? config.team_id : null,
    isLoading,
    refetchFilterConfig: fetchConfig,
  };
}

export default useFilterConfig;
